import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Select, InputNumber, Switch, Space, message, Popconfirm, Tag, Row, Col, Divider, Radio } from 'antd';
import { PlusOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;

const VS_CL = [
  { key: 'cl_pintura', label: 'Pintura' },
  { key: 'cl_placa', label: 'Placa de Identificação' },
  { key: 'cl_obturador', label: 'Obturador e Disco' },
  { key: 'cl_alavanca', label: 'Alavanca de Acionamento' },
  { key: 'cl_haste_mola', label: 'Haste/Mola' },
  { key: 'cl_corpo', label: 'Corpo, Castelo e Capuz' },
  { key: 'cl_anel', label: 'Anel de Regulagem' },
  { key: 'cl_entupimento', label: 'Entupimento' },
  { key: 'cl_conexoes', label: 'Conexões' },
];

function ValvulaForm({ laudoId, tagVaso, origem = 'laudo' }) {
  const [valvulas, setValvulas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [padroes, setPadroes] = useState([]);
  // Campos globais
  const [validadeGlobal, setValidadeGlobal] = useState(1);
  const [padraoGlobal, setPadraoGlobal] = useState({});
  const [padraoIdGlobal, setPadraoIdGlobal] = useState(null);

  useEffect(() => { if (laudoId) carregar(); carregarPadroes(); }, [laudoId]);

  const carregar = async () => {
    try {
      const r = await api.get(`/instrumentos/${laudoId}/valvulas?origem=${origem}`);
      setValvulas(r.data);
      if (r.data.length > 0) {
        setValidadeGlobal(r.data[0].validade_anos || 1);
        // A selecao do padrao tambem precisa voltar: sem isto o campo
        // abria vazio mesmo com o padrao ja gravado no laudo.
        setPadraoIdGlobal(r.data[0].padrao_id || null);
        setPadraoGlobal({
          padrao_identificacao: r.data[0].padrao_identificacao,
          padrao_marca: r.data[0].padrao_marca,
          padrao_certificado: r.data[0].padrao_certificado,
          padrao_calibrado_por: r.data[0].padrao_calibrado_por,
          padrao_validade: r.data[0].padrao_validade,
        });
      }
    } catch {}
  };

  const carregarPadroes = async () => {
    try {
      const r = await api.get('/padroes-calibracao');
      setPadroes(r.data.filter(p => p.tipo === 'manometro' || p.tipo === 'manovacuometro'));
    } catch {}
  };

  const adicionar = async () => {
    if (!laudoId) { message.warning('Salve o laudo primeiro!'); return; }
    try {
      const r = await api.post(`/instrumentos/${laudoId}/valvulas?origem=${origem}`, { 
        tag: '', calibrada: false, pressao_unidade: 'kgf', validade_anos: validadeGlobal, fator_k: 2, local_instalacao: (tagVaso || '').trim() || null,
        ...padraoGlobal, padrao_id: padraoIdGlobal
      });
      setValvulas([...valvulas, r.data]);
    } catch { message.error('Erro ao adicionar válvula'); }
  };

  const salvar = async (vs) => {
    try {
      setLoading(true);
      await api.put(`/instrumentos/${laudoId}/valvulas/${vs.id}?origem=${origem}`, { ...vs, validade_anos: validadeGlobal, ...padraoGlobal, padrao_id: padraoIdGlobal });
      message.success('Válvula salva!');
      carregar();
    } catch { message.error('Erro ao salvar'); }
    finally { setLoading(false); }
  };

  const excluir = async (id) => {
    try {
      await api.delete(`/instrumentos/${laudoId}/valvulas/${id}?origem=${origem}`);
      setValvulas(valvulas.filter(v => v.id !== id));
      message.success('Válvula removida!');
    } catch { message.error('Erro ao excluir'); }
  };

  const gerarPDF = async (vs) => {
    try {
      await api.get(`/laudos/${laudoId}/valvulas/${vs.id}/pdf?origem=${origem}`, { responseType: 'blob' });
      message.success('Certificado de calibração gerado e salvo com sucesso!');
    } catch { message.error('Erro ao gerar PDF'); }
  };

  const atualizar = (id, campo, valor) => {
    setValvulas(valvulas.map(v => v.id === id ? { ...v, [campo]: valor } : v));
  };

  const selecionarPadraoGlobal = (padraoId) => {
    const padrao = padroes.find(p => p.id === padraoId);
    if (padrao) {
      setPadraoIdGlobal(padraoId);
      setPadraoGlobal({
        padrao_identificacao: padrao.identificacao,
        padrao_marca: padrao.marca,
        padrao_certificado: padrao.certificado,
        padrao_calibrado_por: padrao.calibrado_por,
        padrao_validade: padrao.validade,
      });
    }
  };

  if (!laudoId) return <div style={{ padding: 12, background: '#fffbe6', borderRadius: 8 }}>Salve o laudo primeiro para adicionar válvulas.</div>;

  return (
    <div>
      {/* Campos globais */}
      <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 10 }}>Configurações gerais — Válvulas de Segurança</div>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <label style={{ fontWeight: 'bold' }}>Validade (todas):</label>
            <Select value={validadeGlobal} onChange={v => setValidadeGlobal(v)} style={{ width: '100%' }}>
              <Option value={1}>1 ano</Option>
              <Option value={2}>2 anos</Option>
              <Option value={3}>3 anos</Option>
              <Option value={4}>4 anos</Option>
              <Option value={5}>5 anos</Option>
            </Select>
          </Col>
        </Row>
        <div style={{ fontWeight: 'bold', marginTop: 10, marginBottom: 6 }}>Padrão Utilizado (todas):</div>
        {padroes.length > 0 && (
          <Row gutter={8} style={{ marginBottom: 8 }}>
            <Col span={24}>
              <Select placeholder="Selecione o padrão cadastrado" style={{ width: '100%' }} onChange={selecionarPadraoGlobal} value={padraoIdGlobal} allowClear>
                {padroes.map(p => <Option key={p.id} value={p.id}>{p.tipo === 'manometro' ? 'Manômetro' : 'Manovacuômetro'} — {p.identificacao} {p.marca ? '(' + p.marca + ')' : ''}</Option>)}
              </Select>
            </Col>
          </Row>
        )}
        <Row gutter={8}>
          <Col span={6}><label>Identificação</label><Input size="small" value={padraoGlobal.padrao_identificacao||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_identificacao: e.target.value}))} /></Col>
          <Col span={6}><label>Marca</label><Input size="small" value={padraoGlobal.padrao_marca||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_marca: e.target.value}))} /></Col>
          <Col span={6}><label>Certificado Nº</label><Input size="small" value={padraoGlobal.padrao_certificado||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_certificado: e.target.value}))} /></Col>
          <Col span={6}><label>Calibrado por</label><Input size="small" value={padraoGlobal.padrao_calibrado_por||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_calibrado_por: e.target.value}))} /></Col>
        </Row>
      </div>

      <Button icon={<PlusOutlined />} type="dashed" onClick={adicionar} style={{ marginBottom: 16 }}>Adicionar Válvula de Segurança</Button>

      {valvulas.map((vs, idx) => (
        <Card key={vs.id} size="small" style={{ marginBottom: 12 }}
          title={<span>Válvula {idx + 1} {vs.tag ? <Tag color="orange">{vs.tag}</Tag> : ''}</span>}
        >
          <Row gutter={16}>
            <Col span={4}><label>TAG</label><Input value={vs.tag||''} onChange={e => atualizar(vs.id,'tag',e.target.value)} placeholder="VS-01" /></Col>
            <Col span={4}><label>Marca</label><Input value={vs.marca||''} onChange={e => atualizar(vs.id,'marca',e.target.value)} /></Col>
            <Col span={4}><label>Nº Série</label><Input value={vs.numero_serie||''} onChange={e => atualizar(vs.id,'numero_serie',e.target.value)} /></Col>
            <Col span={4}><label>Lacre</label><Input value={vs.lacre||''} onChange={e => atualizar(vs.id,'lacre',e.target.value)} /></Col>
            <Col span={4}><label>Bitola</label><Input value={vs.bitola||''} onChange={e => atualizar(vs.id,'bitola',e.target.value)} /></Col>
            <Col span={4}><label>Local Instalação</label><Input value={vs.local_instalacao||''} onChange={e => atualizar(vs.id,'local_instalacao',e.target.value)} placeholder="VP-01" /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={4}><label>Pressão Ajuste</label><InputNumber value={vs.pressao_ajuste} onChange={v => atualizar(vs.id,'pressao_ajuste',v)} style={{ width: '100%' }} decimalSeparator="," precision={2} /></Col>
            <Col span={4}><label>Unidade</label>
              <Select value={vs.pressao_unidade||'kgf'} onChange={v => atualizar(vs.id,'pressao_unidade',v)} style={{ width: '100%' }}>
                <Option value="kgf">kgf/cm²</Option><Option value="bar">bar</Option><Option value="psi">psi</Option>
              </Select>
            </Col>
            <Col span={4}><label>Fluido Ajuste</label><Input value={vs.fluido_ajuste||''} onChange={e => atualizar(vs.id,'fluido_ajuste',e.target.value)} /></Col>
            <Col span={4}><label>Conexão</label>
              <Select value={vs.conexao||''} onChange={v => atualizar(vs.id,'conexao',v)} style={{ width: '100%' }}>
                <Option value="rosca">Rosca</Option><Option value="flange">Flange</Option>
              </Select>
            </Col>
            <Col span={4}><label>Tipo</label>
              <Select value={vs.tipo||''} onChange={v => atualizar(vs.id,'tipo',v)} style={{ width: '100%' }}>
                <Option value="alavanca">Alavanca</Option><Option value="bloco">Bloco</Option><Option value="argola">Argola</Option>
              </Select>
            </Col>
            <Col span={4}><label>Banca</label><Input value={vs.banca||''} onChange={e => atualizar(vs.id,'banca',e.target.value)} /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={24}>
              <label style={{ fontWeight: 'bold' }}>Observações:</label>
              <Input.TextArea value={vs.observacoes||''} onChange={e => atualizar(vs.id,'observacoes',e.target.value)} rows={2} style={{ marginTop: 4 }} />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={4}><label>Calibrada?</label><br/><Switch checked={vs.calibrada} onChange={v => atualizar(vs.id,'calibrada',v)} checkedChildren="Sim" unCheckedChildren="Não" /></Col>
            <Col span={4}><label>Danificada?</label><br/><Switch checked={vs.danificada} onChange={v => atualizar(vs.id,'danificada',v)} checkedChildren="Sim" unCheckedChildren="Não" /></Col>
            <Col span={4}><label>Substituída?</label><br/><Switch checked={vs.substituida} onChange={v => atualizar(vs.id,'substituida',v)} checkedChildren="Sim" unCheckedChildren="Não" /></Col>
            <Col span={4}><label>Desmontou?</label><br/><Switch checked={vs.desmontou} onChange={v => atualizar(vs.id,'desmontou',v)} checkedChildren="Sim" unCheckedChildren="Não" /></Col>
            <Col span={4}><label>Resultado</label>
              <Select value={vs.resultado_laudo||''} onChange={v => atualizar(vs.id,'resultado_laudo',v)} style={{ width: '100%' }}
                placeholder="Automático">
                <Option value="">Automático</Option>
                <Option value="APROVADO">Aprovado</Option>
                <Option value="REPROVADO">Reprovado</Option>
              </Select>
            </Col>
          </Row>
          {vs.calibrada && (
            <div style={{ marginTop: 12, padding: 12, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
              <Row gutter={16} style={{ marginBottom: 8 }}>
                <Col span={6}><label style={{ fontWeight: 'bold' }}>Unidade das leituras:</label>
                  <Select value={vs.unidade_calibracao||'kgf'} onChange={v => atualizar(vs.id,'unidade_calibracao',v)} style={{ width: '100%' }}>
                    <Option value="kgf">kgf/cm²</Option><Option value="bar">bar</Option><Option value="psi">psi</Option><Option value="mpa">MPa</Option>
                  </Select>
                </Col>
                <Col span={4}>
                  <label style={{ fontWeight: 'bold' }}>Fator K:</label>
                  <InputNumber value={vs.fator_k ?? 2} onChange={v => atualizar(vs.id,'fator_k',v)} style={{ width: '100%' }} precision={0} min={1} max={3} />
                </Col>
                <Col span={6}>
                  <label style={{ fontWeight: 'bold' }}>Incerteza expandida (U = k × u):</label>
                  {(() => {
                    const erros = [];
                    for (let i = 1; i <= 5; i++) {
                      const ab = vs[`abertura_${i}`];
                      const fec = vs[`fechamento_${i}`];
                      const ref = vs.pressao_ajuste;
                      if (ref != null && ab != null) erros.push(ab - ref);
                      if (ref != null && fec != null) erros.push(fec - ref);
                    }
                    if (erros.length < 2) return <div style={{ color: '#aaa', fontSize: 12 }}>Preencha as leituras</div>;
                    const media = erros.reduce((a, b) => a + b, 0) / erros.length;
                    const variancia = erros.reduce((a, b) => a + Math.pow(b - media, 2), 0) / (erros.length - 1);
                    const u = Math.sqrt(variancia);
                    const k = vs.fator_k ?? 2;
                    const U = k * u;
                    return <div style={{ fontWeight: 'bold', fontSize: 14, color: '#1a6ca8', padding: '4px 0' }}>U = {U.toFixed(4).replace('.', ',')} {vs.unidade_calibracao || 'kgf'}</div>;
                  })()}
                </Col>
              </Row>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 6 }}>Leituras de Calibração</label>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#1a6ca8', color: 'white' }}>
                    <th style={{ padding: '4px 8px', border: '1px solid #ccc' }}>Leitura</th>
                    <th style={{ padding: '4px 8px', border: '1px solid #ccc' }}>Abertura ({vs.unidade_calibracao||'kgf'})</th>
                    <th style={{ padding: '4px 8px', border: '1px solid #ccc' }}>Fechamento ({vs.unidade_calibracao||'kgf'})</th>
                  </tr>
                </thead>
                <tbody>
                  {[1,2,3,4,5].map(i => (
                    <tr key={i}>
                      <td style={{ padding: 4, border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold' }}>{i}</td>
                      <td style={{ padding: 4, border: '1px solid #ddd' }}><InputNumber value={vs[`abertura_${i}`]} onChange={v => atualizar(vs.id,`abertura_${i}`,v)} style={{ width: '100%' }} precision={3} decimalSeparator="," /></td>
                      <td style={{ padding: 4, border: '1px solid #ddd' }}><InputNumber value={vs[`fechamento_${i}`]} onChange={v => atualizar(vs.id,`fechamento_${i}`,v)} style={{ width: '100%' }} precision={3} decimalSeparator="," /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <label style={{ fontWeight: 'bold', marginBottom: 8, display: 'block' }}>Check List Válvula</label>
            {VS_CL.map(item => (
              <Row key={item.key} align="middle" style={{ marginBottom: 4, borderBottom: '1px solid #f0f0f0', padding: '2px 0' }}>
                <Col span={12}>{item.label}</Col>
                <Col span={12}>
                  <Radio.Group value={vs[item.key]||''} onChange={e => atualizar(vs.id, item.key, e.target.value)}>
                    <Radio value="C" style={{ color: 'green' }}>C</Radio>
                    <Radio value="NC" style={{ color: 'red' }}>N/C</Radio>
                    <Radio value="NA" style={{ color: 'gray' }}>N/A</Radio>
                  </Radio.Group>
                </Col>
              </Row>
            ))}
          </div>
          <Divider />
          <Space>
            <Button size="small" icon={<FilePdfOutlined />} style={{ color: '#cf1322', borderColor: '#cf1322' }} onClick={() => gerarPDF(vs)}>Gerar Laudo Calibração</Button>
            <Button size="small" type="primary" onClick={() => salvar(vs)} loading={loading}>Salvar</Button>
            <Popconfirm title="Excluir válvula?" onConfirm={() => excluir(vs.id)} okText="Sim" cancelText="Não">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        </Card>
      ))}
    </div>
  );
}

export default ValvulaForm;
